import { Alert, AlertTitle, Box, IconButton, MenuItem, Select } from "@mui/material"
import { CloseOutlined, WarningOutlined } from "@mui-symbols-material/w300"
import { type FC, useState } from "react"
import { useShallow } from "zustand/shallow"

import PalettesRoundControl from "./PalettesRoundControl"

import type { Product } from "@/graphql/codegen/graphql"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"
import colors from "@/styles/colors"
import { RoundOffType, RoundPrecisionUnit } from "@/types/rounding"
import { calculateRoundingValues } from "@/utils/purchaseOrder"

export interface RoundOffResult {
  roundOffType: RoundOffType
  precision: RoundPrecisionUnit
  quantity: number
  pallets: number
}

interface RoundOffWarningProps {
  product: Product
  uomId: string
  pallets: number
  quantity: number
  isPrecisionLocked: boolean
  onRound: (result: RoundOffResult | null) => void
}

export const RoundOffWarning: FC<RoundOffWarningProps> = ({
  product,
  uomId,
  pallets,
  quantity,
  isPrecisionLocked,
  onRound,
}) => {
  const [roundPrecisionUnit, setRoundPrecisionUnit] = useState<RoundPrecisionUnit>(RoundPrecisionUnit.Pallet)

  const units = useUnitOfMeasurementsStore(useShallow((state) => state.units))

  const { lowerWeight, upperWeight, lowerNumber, upperNumber } = calculateRoundingValues({
    palletsNumber: pallets,
    quantityInUom: quantity,
    uomId,
    product,
    roundPrecisionUnit,
  })
  const uom = units.find((u) => u.id === uomId)?.name ?? ""
  const handleRoundOffChange = (roundOffType: RoundOffType | null) => {
    if (roundOffType === null) {
      onRound(null)
      return
    }

    const isUp = roundOffType === RoundOffType.UP
    onRound({
      roundOffType,
      precision: roundPrecisionUnit,
      quantity: isUp ? upperWeight : lowerWeight,
      pallets: isUp ? upperNumber : lowerNumber,
    })
  }

  return (
    <Box mx={4}>
      <Alert
        severity={"warning"}
        variant='filled'
        className='bg-[#FFF4E5] text-[#663C00]'
        icon={<WarningOutlined className='text-[#EF6C00]' />}
        action={
          <Box className='flex items-center gap-2'>
            <Box className='flex gap-4 rounded-md border border-tertiary-orange-700 px-3 text-gray-800'>
              <Box className='border-r border-tertiary-orange-700' data-testid='round-precision-unit-select'>
                <Select<RoundPrecisionUnit>
                  labelId='precision-select-label'
                  id='precision-select'
                  value={roundPrecisionUnit}
                  disabled={isPrecisionLocked}
                  onChange={(v) => setRoundPrecisionUnit(v.target.value as RoundPrecisionUnit)}
                  sx={{
                    width: "120px",
                    height: "100%",
                    border: "none",
                    ".MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "#FFF4E5",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.tertiary.orange[700],
                    },
                  }}
                >
                  <MenuItem value={RoundPrecisionUnit.Pallet}>Pallets</MenuItem>
                  <MenuItem value={RoundPrecisionUnit.Bag}>Bags</MenuItem>
                </Select>
              </Box>
              {lowerNumber > 0 && (
                <PalettesRoundControl
                  uom={uom}
                  roundOffType={RoundOffType.DOWN}
                  palettesNumber={lowerNumber}
                  palettesWeight={lowerWeight}
                  onClick={() => handleRoundOffChange(RoundOffType.DOWN)}
                />
              )}
              <PalettesRoundControl
                uom={uom}
                roundOffType={RoundOffType.UP}
                palettesNumber={upperNumber}
                palettesWeight={upperWeight}
                onClick={() => handleRoundOffChange(RoundOffType.UP)}
              />
            </Box>
            <IconButton
              aria-label='close'
              size='small'
              className='text-[#663C00]'
              onClick={() => handleRoundOffChange(null)}
            >
              <CloseOutlined fontSize='small' />
            </IconButton>
          </Box>
        }
        data-testid='round-off-warning'
      >
        <AlertTitle>The order quantity does not add up to an exact number of pallets</AlertTitle>
        You can round down or round up the order quantity, or leave as is.
      </Alert>
    </Box>
  )
}
